import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui';

import { meetingStore } from '../meetings.state';

import type { MeetingId } from '../meetings.types';

export const CopyTranscriptButton = observer(function CopyTranscriptButton({
  meetingId,
}: {
  meetingId: MeetingId;
}) {
  const [justCopied, setJustCopied] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transcript = meetingStore
    .getTranscriptLines(meetingId)
    .join('\n')
    .trim();

  useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    []
  );

  const handleCopy = useCallback(async () => {
    if (!transcript) return;
    try {
      await navigator.clipboard.writeText(transcript);
      setJustCopied(true);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setJustCopied(false), 1500);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  }, [transcript]);

  if (!transcript) return null;

  return (
    <IconButton
      size="sm"
      icon={justCopied ? Icons.check : Icons.copy}
      tooltip={justCopied ? 'Copied' : 'Copy transcript'}
      onClick={handleCopy}
    />
  );
});
